/**
 * Chutt... — useFreshnessCheck hook
 * Detects when CRON has produced fresh data and invalidates localStorage caches.
 * Non-blocking: fires async, never shows a spinner, never delays page render.
 *
 * Returns:
 *   - lastUpdated: Date | null (timestamp of last CRON collection)
 *   - cacheVersion: number (increments on each invalidation — use as useEffect dep)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { checkAndSync } from '../services/freshness';

export default function useFreshnessCheck(brandId) {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cacheVersion, setCacheVersion] = useState(0);
  const runningRef = useRef(false);
  const lastBrandRef = useRef(null);

  const runCheck = useCallback(async () => {
    if (!brandId || runningRef.current) return;

    runningRef.current = true;
    try {
      const result = await checkAndSync(brandId);
      setLastUpdated(result.lastUpdated);
      if (result.invalidated) {
        setCacheVersion(v => v + 1);
      }
    } catch (_) {
      // Silent fail — no degradation
    } finally {
      runningRef.current = false;
    }
  }, [brandId]);

  useEffect(() => {
    // Run on mount and on brand change
    if (brandId && brandId !== lastBrandRef.current) {
      lastBrandRef.current = brandId;
      runCheck();
    }
  }, [brandId, runCheck]);

  return { lastUpdated, cacheVersion };
}
